import React, { useState, useEffect, useCallback } from 'react';
import { IonContent, IonPage, IonHeader, IonToolbar, IonTitle, IonButtons, IonToast, IonModal } from '@ionic/react';
import { useHistory } from 'react-router-dom';
import { ChevronLeft, Users, Shield, Heart, Plus, Trash2, Edit3, Baby, User, Loader2, ArrowRight } from 'lucide-react';
import { capacitorDbService } from '../services/capacitorDb.service';
import { useAuthStore } from '../store/authStore';
import { triggerWealthSync } from '../hooks/useWealthData';

interface FamilyMember {
  id: string;
  name: string;
  relationship: string;
  age: number;
  dependent: boolean;
}

const RELATIONSHIPS = [
  { id: 'spouse', label: 'Spouse', icon: Heart },
  { id: 'child', label: 'Child', icon: Baby },
  { id: 'parent', label: 'Parent', icon: User },
  { id: 'sibling', label: 'Sibling', icon: Users },
  { id: 'other', label: 'Other', icon: User },
];

const FamilyScreen: React.FC = () => {
  const history = useHistory();
  const { user } = useAuthStore();

  const [members, setMembers] = useState<FamilyMember[]>([]);
  const [takafulPlan, setTakafulPlan] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editingMember, setEditingMember] = useState<FamilyMember | null>(null);
  const [formName, setFormName] = useState('');
  const [formRelationship, setFormRelationship] = useState('');
  const [formAge, setFormAge] = useState('');
  const [formDependent, setFormDependent] = useState(true);

  const getCurrentUserId = useCallback(async (): Promise<string | null> => {
    if (user?.id) return user.id;
    const globalId = (window as any).__NURWALLET_USER_ID__;
    if (globalId) return globalId;
    try {
      await capacitorDbService.init();
      const guestUser = await capacitorDbService.getUserByEmail('guest@nurwallet.local');
      if (guestUser) { (window as any).__NURWALLET_USER_ID__ = guestUser.id; return guestUser.id; }
    } catch (e) {}
    try {
      const allUsers = await capacitorDbService.getAllUsers?.();
      if (allUsers?.length > 0) { (window as any).__NURWALLET_USER_ID__ = allUsers[0].id; return allUsers[0].id; }
    } catch (e) {}
    return null;
  }, [user?.id]);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const userId = await getCurrentUserId();
      if (!userId) return;

      // For now, family members stored in localStorage (no DB table yet)
      const saved = localStorage.getItem(`family_members_${userId}`);
      if (saved) setMembers(JSON.parse(saved));

      const takaful = await capacitorDbService.getTakafulPlan(userId).catch(() => null);
      setTakafulPlan(takaful);
    } catch (error) {
      console.error('[Family] Load error:', error);
    } finally {
      setIsLoading(false);
    }
  }, [getCurrentUserId]);

  useEffect(() => { loadData(); }, [loadData]);

  const saveMembers = async (newMembers: FamilyMember[]) => {
    const userId = await getCurrentUserId();
    if (!userId) return;
    localStorage.setItem(`family_members_${userId}`, JSON.stringify(newMembers));
    setMembers(newMembers);
  };

  const handleSaveMember = async () => {
    if (!formName || !formRelationship || !formAge) return;

    const newMember: FamilyMember = {
      id: editingMember?.id || `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: formName,
      relationship: formRelationship,
      age: Number(formAge),
      dependent: formDependent,
    };

    const updated = editingMember 
      ? members.map(m => m.id === editingMember.id ? newMember : m)
      : [...members, newMember];

    await saveMembers(updated);
    triggerWealthSync();
    setShowModal(false);
    resetForm();
    showMsg('✅ Family member saved');
  };

  const handleDelete = async (id: string) => {
    const updated = members.filter(m => m.id !== id);
    await saveMembers(updated);
    triggerWealthSync();
    showMsg('✅ Member removed');
  };

  const resetForm = () => {
    setFormName(''); setFormRelationship(''); setFormAge(''); setFormDependent(true); setEditingMember(null);
  };

  const showMsg = (msg: string) => { setToastMessage(msg); setShowToast(true); };

  const openModal = (member?: FamilyMember) => {
    if (member) {
      setEditingMember(member);
      setFormName(member.name);
      setFormRelationship(member.relationship);
      setFormAge(String(member.age));
      setFormDependent(member.dependent);
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const dependents = members.filter(m => m.dependent);
  const coveragePerDependent = takafulPlan?.recommendedCoverage 
    ? Math.round((takafulPlan.recommendedCoverage * 0.6) / Math.max(dependents.length, 1))
    : 0;

  if (isLoading) {
    return (
      <IonPage>
        <IonContent style={{ '--background': '#f9fafb' } as any}>
          <div className="flex items-center justify-center h-full">
            <Loader2 size={32} className="text-emerald-600 animate-spin" />
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonHeader className="ion-no-border">
        <IonToolbar style={{ '--background': '#f9fafb' } as any}>
          <IonButtons slot="start">
            <button onClick={() => history.push('/home')} className="p-2 -ml-2 rounded-full hover:bg-gray-100 transition">
              <ChevronLeft size={24} className="text-gray-800" />
            </button>
          </IonButtons>
          <IonTitle className="text-lg font-bold">Family Protection</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent style={{ '--background': '#f9fafb' } as any}>
        {/* Takaful Summary */}
        {takafulPlan && (
          <div className="px-5 pt-2 pb-4">
            <div className="bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl p-5 text-white">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <Shield size={20} />
                </div>
                <div>
                  <p className="text-xs text-blue-200">Total Coverage</p>
                  <h2 className="text-xl font-bold">RM {takafulPlan.recommendedCoverage?.toLocaleString() || 0}</h2>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white/20 rounded-xl p-3">
                  <p className="text-xs text-blue-200">Existing</p>
                  <p className="text-lg font-bold">RM {takafulPlan.existingCoverage?.toLocaleString() || 0}</p>
                </div>
                <div className="bg-white/20 rounded-xl p-3">
                  <p className="text-xs text-blue-200">Gap</p>
                  <p className="text-lg font-bold text-amber-300">
                    RM {Math.max(0, (takafulPlan.recommendedCoverage || 0) - (takafulPlan.existingCoverage || 0)).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Dependents Summary */}
        <div className="px-5 mb-4">
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-800">Dependents</h3>
              <span className="text-xs px-2 py-1 bg-emerald-100 text-emerald-600 rounded-full font-medium">
                {dependents.length} dependent{dependents.length !== 1 ? 's' : ''}
              </span>
            </div>
            {dependents.length > 0 && takafulPlan && (
              <p className="text-xs text-gray-500 mb-2">
                Recommended coverage per dependent: RM {coveragePerDependent.toLocaleString()}
              </p>
            )}
            <button 
              onClick={() => history.push('/takaful')} 
              className="w-full py-2.5 rounded-xl bg-blue-50 text-blue-600 text-sm font-medium flex items-center justify-center gap-2"
            >
              <Shield size={16} /> Review Takaful Coverage <ArrowRight size={14} />
            </button>
          </div>
        </div>

        {/* Family Members */}
        <div className="px-5 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-gray-800">Family Members</h2>
            <button onClick={() => openModal()} className="px-3 py-1.5 bg-emerald-600 text-white rounded-full text-xs font-medium flex items-center gap-1">
              <Plus size={14} /> Add
            </button>
          </div>

          {members.length === 0 ? (
            <div className="bg-white rounded-xl p-6 text-center border border-gray-100">
              <Users size={32} className="text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No family members added yet.</p>
              <p className="text-xs text-gray-400 mt-1">Add your spouse, children, parents to track protection needs.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {members.map((member) => {
                const relInfo = RELATIONSHIPS.find(r => r.id === member.relationship) || RELATIONSHIPS[4];
                const Icon = relInfo.icon;
                return (
                  <div key={member.id} className="bg-white rounded-xl p-4 border border-gray-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                          <Icon size={18} className="text-emerald-600" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-800">{member.name}</p>
                          <p className="text-xs text-gray-500">{relInfo.label} · {member.age} years old</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {member.dependent && (
                          <span className="px-2 py-0.5 bg-amber-100 text-amber-600 rounded-full text-[10px] font-medium">
                            Dependent
                          </span>
                        )}
                        <button onClick={() => openModal(member)} className="p-1 text-gray-400 hover:text-emerald-600">
                          <Edit3 size={14} />
                        </button>
                        <button onClick={() => handleDelete(member.id)} className="p-1 text-gray-400 hover:text-red-500">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Protection Tips */}
        <div className="px-5 mb-6">
          <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
            <h3 className="text-sm font-bold text-amber-800 mb-2 flex items-center gap-2">
              <Shield size={16} /> Protection Tips
            </h3>
            <ul className="space-y-2 text-xs text-amber-700">
              <li className="flex items-start gap-2">
                <span className="w-1 h-1 rounded-full bg-amber-500 mt-1.5 flex-shrink-0" />
                Each dependent should have coverage of at least 5x annual income
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1 h-1 rounded-full bg-amber-500 mt-1.5 flex-shrink-0" />
                Review your takaful coverage annually or when family changes
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1 h-1 rounded-full bg-amber-500 mt-1.5 flex-shrink-0" />
                Consider education fund for children in your protection plan
              </li>
            </ul>
          </div>
        </div>

        <div className="h-20" />
      </IonContent>

      {/* Member Modal */}
      <IonModal isOpen={showModal} onDidDismiss={() => { setShowModal(false); resetForm(); }}>
        <div className="p-5 bg-white h-full">
          <h2 className="text-xl font-bold text-gray-800 mb-4">{editingMember ? 'Edit Member' : 'Add Family Member'}</h2>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-gray-600">Name</label>
              <input value={formName} onChange={e => setFormName(e.target.value)} className="w-full mt-1 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5" placeholder="e.g., Sarah" />
            </div>
            <div>
              <label className="text-sm text-gray-600">Relationship</label>
              <select value={formRelationship} onChange={e => setFormRelationship(e.target.value)} className="w-full mt-1 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5">
                <option value="">Select relationship</option>
                {RELATIONSHIPS.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm text-gray-600">Age</label>
              <input type="number" value={formAge} onChange={e => setFormAge(e.target.value)} className="w-full mt-1 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5" placeholder="25" />
            </div>
            <div className="flex items-center gap-3">
              <input type="checkbox" checked={formDependent} onChange={e => setFormDependent(e.target.checked)} className="w-5 h-5 rounded border-gray-300" />
              <label className="text-sm text-gray-600">Is a dependent (relies on your income)</label>
            </div>
          </div>
          <div className="flex gap-3 mt-6">
            <button onClick={() => { setShowModal(false); resetForm(); }} className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-600 font-medium">Cancel</button>
            <button onClick={handleSaveMember} className="flex-1 py-3 rounded-xl bg-emerald-600 text-white font-medium">Save</button>
          </div>
        </div>
      </IonModal>

      <IonToast isOpen={showToast} onDidDismiss={() => setShowToast(false)} message={toastMessage} duration={2000} position="top" color="success" />
    </IonPage>
  );
};

export default FamilyScreen;
